/**
 * Single source of truth for paid-ad visitor detection (Google Ads).
 *
 * Why this module exists:
 * - `useSearchParams()` is empty on `force-static` pages, so React never saw gclid.
 * - The previous `sessionStorage` flag was tab-scoped and was written but never
 *   subscribed to, so CTAs never flipped.
 *
 * One key (`pickcel_is_ad_session`) is written from the landing URL (or a prior
 * capture) into localStorage with an explicit TTL aligned to Google's `_gcl_aw`
 * (~90 days). `useIsAdLanding` is the only React reader.
 */

export const AD_SESSION_KEY = "pickcel_is_ad_session";
export const AD_SESSION_CHANGE_EVENT = "pickcel-ad-session-change";

/** Match Google Ads `_gcl_aw` conversion-attribution window. */
export const AD_SESSION_TTL_DAYS = 90;
export const AD_SESSION_TTL_MS = AD_SESSION_TTL_DAYS * 24 * 60 * 60 * 1000;

export const GOOGLE_ADS_UTM_SOURCE = "Google_Ads";
export const CHATGPT_ADS_UTM_SOURCE = "ChatGPT_Ads";
export const AD_OPPREF_PARAM = "oppref";
export const AD_CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid"] as const;
export const AD_SOURCE_PARAMS = ["gad_source"] as const;

export type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

type AdSessionRecord = {
  v: true;
  expires_at: string;
};

const toSearchParams = (search: string | URLSearchParams): URLSearchParams => {
  if (typeof search !== "string") return search;
  const trimmed = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(trimmed);
};

/** True when the URL itself is a Google Ads or ChatGPT Ads landing (any trigger, independently). */
export function isAdLandingSearch(search: string | URLSearchParams): boolean {
  const params = toSearchParams(search);
  if (params.get(AD_OPPREF_PARAM)) return true;
  const utmSource = params.get("utm_source");
  if (utmSource === GOOGLE_ADS_UTM_SOURCE) return true;
  // Exact, case-insensitive match only — never a substring/prefix check, or
  // utm_source=chatgpt.com (organic ChatGPT referral traffic) would wrongly match too.
  if (utmSource && utmSource.toLowerCase() === CHATGPT_ADS_UTM_SOURCE.toLowerCase()) return true;
  if (AD_CLICK_ID_PARAMS.some((key) => Boolean(params.get(key)))) return true;
  if (AD_SOURCE_PARAMS.some((key) => Boolean(params.get(key)))) return true;
  return false;
}

export function serializeAdSessionRecord(now = Date.now()): string {
  const record: AdSessionRecord = {
    v: true,
    expires_at: new Date(now + AD_SESSION_TTL_MS).toISOString(),
  };
  return JSON.stringify(record);
}

export function isStoredAdSessionRecord(raw: string | null, now = Date.now()): boolean {
  if (!raw) return false;
  if (raw === "true") return true;
  try {
    const parsed = JSON.parse(raw) as Partial<AdSessionRecord>;
    if (parsed?.v !== true || !parsed.expires_at) return false;
    return new Date(parsed.expires_at).getTime() > now;
  } catch {
    return false;
  }
}

const getLocalStorage = (storage?: StorageLike | null): StorageLike | null => {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const notifyAdSessionChange = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AD_SESSION_CHANGE_EVENT));
};

export function writeAdSession(storage?: StorageLike | null, now = Date.now()): void {
  const local = getLocalStorage(storage);
  if (!local) return;
  try {
    local.setItem(AD_SESSION_KEY, serializeAdSessionRecord(now));
    notifyAdSessionChange();
  } catch {
    // Privacy modes / full storage
  }
}

/** Pure read — safe for `useSyncExternalStore` snapshots. */
export function peekAdSession(storage?: StorageLike | null, now = Date.now()): boolean {
  const local = getLocalStorage(storage);
  if (local) {
    try {
      if (isStoredAdSessionRecord(local.getItem(AD_SESSION_KEY), now)) return true;
    } catch {
      // ignore
    }
  }
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(AD_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

export function readAdSession(storage?: StorageLike | null, now = Date.now()): boolean {
  const local = getLocalStorage(storage);
  if (local) {
    try {
      const raw = local.getItem(AD_SESSION_KEY);
      if (isStoredAdSessionRecord(raw, now)) {
        if (raw === "true") writeAdSession(local, now);
        return true;
      }
      if (raw) local.removeItem(AD_SESSION_KEY);
    } catch {
      // ignore
    }
  }

  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(AD_SESSION_KEY) === "true") {
      writeAdSession(local, now);
      try {
        window.sessionStorage.removeItem(AD_SESSION_KEY);
      } catch {
        // ignore
      }
      return true;
    }
  } catch {
    // ignore
  }

  return false;
}

/**
 * Persist the ad-session flag when the current URL is a paid landing.
 * Does not clear an existing (still-valid) flag on organic follow-up visits.
 * Returns whether the visitor should be treated as an ad session.
 */
export function captureAdSessionFromSearch(
  search: string | URLSearchParams,
  storage?: StorageLike | null,
  now = Date.now()
): boolean {
  if (isAdLandingSearch(search)) {
    writeAdSession(storage, now);
    return true;
  }
  return readAdSession(storage, now);
}

export function captureAdSessionFromLocation(): boolean {
  if (typeof window === "undefined") return false;
  return captureAdSessionFromSearch(window.location.search);
}

/** Client snapshot for `useSyncExternalStore`. URL is a live fallback; storage is the persist. */
export function getAdSessionSnapshot(): boolean {
  if (typeof window === "undefined") return false;
  if (peekAdSession()) return true;
  return isAdLandingSearch(window.location.search);
}

export function subscribeAdSession(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(AD_SESSION_CHANGE_EVENT, onStoreChange);
  window.addEventListener("storage", onStoreChange);
  return () => {
    window.removeEventListener(AD_SESSION_CHANGE_EVENT, onStoreChange);
    window.removeEventListener("storage", onStoreChange);
  };
}

/** Blocking boot script: capture from `window.location` before React hydrates. */
export function getAdSessionBootScript(): string {
  return `(function(){try{
    var KEY=${JSON.stringify(AD_SESSION_KEY)};
    var TTL=${AD_SESSION_TTL_MS};
    var params=new URLSearchParams(window.location.search);
    var utmSource=params.get("utm_source")||"";
    var isAd=!!params.get(${JSON.stringify(AD_OPPREF_PARAM)})
      ||utmSource===${JSON.stringify(GOOGLE_ADS_UTM_SOURCE)}
      ||utmSource.toLowerCase()===${JSON.stringify(CHATGPT_ADS_UTM_SOURCE.toLowerCase())}
      ||${JSON.stringify(AD_CLICK_ID_PARAMS)}.some(function(k){return params.get(k);})
      ||${JSON.stringify(AD_SOURCE_PARAMS)}.some(function(k){return params.get(k);});
    var stored=false;
    try{
      var raw=window.localStorage.getItem(KEY);
      if(raw==="true")stored=true;
      else if(raw){
        var parsed=JSON.parse(raw);
        stored=!!(parsed&&parsed.v===true&&parsed.expires_at&&new Date(parsed.expires_at).getTime()>Date.now());
      }
    }catch(e){}
    if(isAd){
      window.localStorage.setItem(KEY,JSON.stringify({v:true,expires_at:new Date(Date.now()+TTL).toISOString()}));
      stored=true;
    }
    if(stored)document.documentElement.setAttribute("data-pickcel-ad-session","true");
  }catch(e){}})();`;
}

/** Inline reader used by UTMAppendScript so click-time gating uses the same key. */
export function getAdSessionReadSnippet(): string {
  return `function isPickcelAdSession(){
    try {
      var raw=window.localStorage.getItem(${JSON.stringify(AD_SESSION_KEY)});
      if(!raw){
        try{return window.sessionStorage.getItem(${JSON.stringify(AD_SESSION_KEY)})==="true";}catch(_){return false;}
      }
      if(raw==="true")return true;
      var persisted=JSON.parse(raw);
      if(!persisted||persisted.v!==true||!persisted.expires_at)return false;
      return new Date(persisted.expires_at).getTime()>Date.now();
    }catch(_){return false;}
  }`;
}
